
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { PlusCircle, Edit, Trash2, Filter } from 'lucide-react';
import { MOCK_STORIES, Story } from '@/utils/storiesData';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from "@/components/ui/use-toast";

const AdminPanel = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState<Story[]>(MOCK_STORIES);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState<Story | null>(null);
  const [filterCategory, setFilterCategory] = useState<string | null>(null);

  // Derive categories from stories for filter dropdown
  const categories = Array.from(new Set(stories.map(story => story.category)));

  // Filter stories based on selected category
  const filteredStories = filterCategory 
    ? stories.filter(story => story.category === filterCategory)
    : stories;

  const handleCreateStory = () => {
    navigate('/admin/stories/new');
  };

  const handleEditStory = (id: string) => {
    navigate(`/admin/stories/edit/${id}`);
  };

  const handleDeleteStory = (story: Story) => {
    setStoryToDelete(story);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (storyToDelete) {
      // In a real app, this would be an API call
      setStories(stories.filter(story => story.id !== storyToDelete.id));
      toast({
        title: "Story deleted",
        description: `"${storyToDelete.title}" has been deleted.`,
      });
    }
    setDeleteDialogOpen(false);
    setStoryToDelete(null);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold font-gt-super">Content Management System</h1>
          <div className="flex space-x-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  {filterCategory || "All Categories"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setFilterCategory(null)}>
                  All Categories
                </DropdownMenuItem>
                {categories.map(category => (
                  <DropdownMenuItem 
                    key={category} 
                    onClick={() => setFilterCategory(category)}
                  >
                    {category}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button onClick={handleCreateStory}>
              <PlusCircle className="h-4 w-4 mr-2" />
              New Story
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead className="hidden md:table-cell">Author</TableHead>
                <TableHead className="hidden md:table-cell">Published</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStories.map((story) => (
                <TableRow key={story.id}>
                  <TableCell className="font-medium">{story.id}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <div className="h-10 w-10 rounded overflow-hidden">
                        <img 
                          src={story.coverImage} 
                          alt={story.title} 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <span className="font-medium truncate max-w-[200px]">{story.title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{story.category}</TableCell>
                  <TableCell className="hidden md:table-cell">{story.author.name}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {new Date(story.publishedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEditStory(story.id)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteStory(story)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-gt-super">Delete Story</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{storyToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminPanel;

